import { useCallback, useEffect, useState } from "react";
import { type SummaryJob, fetchSummaryJobs } from "../../api/admin-content";
import { PageHeader } from "../../components/PageHeader";
import {
  ErrorBanner,
  LoadingIndicator,
  Pagination,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import { SummaryJobDetailPanel } from "./SummaryJobDetailPanel";

const PAGE_SIZE = 20;

type StatusFilter = "all" | "pending" | "running" | "completed" | "failed";

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "summary_jobs.filter_all" },
  { value: "pending", label: "summary_jobs.filter_pending" },
  { value: "running", label: "summary_jobs.filter_running" },
  { value: "completed", label: "summary_jobs.filter_completed" },
  { value: "failed", label: "summary_jobs.filter_failed" },
];

export function SummaryJobsPage(): React.JSX.Element {
  const [items, setItems] = useState<ReadonlyArray<SummaryJob>>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSummaryJobs({
        limit: PAGE_SIZE,
        offset,
        status: status === "all" ? undefined : status,
        user_id: userIdFilter.trim() || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [offset, status, userIdFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleStatusChange = useCallback((s: StatusFilter): void => {
    setStatus(s);
    setOffset(0);
    setExpandedId(null);
  }, []);

  const handleUserSearch = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault();
      setOffset(0);
      setExpandedId(null);
      void load();
    },
    [load],
  );

  const handleToggle = useCallback((id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div>
      <PageHeader
        title={t("summary_jobs.title")}
        description={t("summary_jobs.description")}
      />
      <div style={styles.controls}>
        <StatusBar active={status} onChange={handleStatusChange} />
        <form onSubmit={handleUserSearch} style={styles.searchForm}>
          <input
            type="text"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            placeholder={t("summary_jobs.user_id_placeholder")}
            style={ts.searchInput}
          />
          <button type="submit" style={ts.searchBtn}>
            Filter
          </button>
        </form>
      </div>
      {error && <ErrorBanner message={error} />}
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <JobsTable
            items={items}
            expandedId={expandedId}
            onToggle={handleToggle}
            onRetryDone={load}
          />
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

// ─── Sub-components ───

function StatusBar({
  active,
  onChange,
}: {
  active: StatusFilter;
  onChange: (s: StatusFilter) => void;
}): React.JSX.Element {
  return (
    <div style={ts.filterBar}>
      {STATUS_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          style={{
            ...ts.filterBtn,
            ...(active === o.value ? ts.filterBtnActive : {}),
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function JobsTable({
  items,
  expandedId,
  onToggle,
  onRetryDone,
}: {
  items: ReadonlyArray<SummaryJob>;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onRetryDone: () => void;
}): React.JSX.Element {
  if (items.length === 0) {
    return <p style={ts.emptyText}>{t("summary_jobs.no_jobs_found")}</p>;
  }

  return (
    <table style={ts.table}>
      <thead>
        <tr>
          <th style={ts.th}>{t("summary_jobs.col_target")}</th>
          <th style={ts.th}>{t("summary_jobs.col_user")}</th>
          <th style={ts.th}>{t("summary_jobs.col_status")}</th>
          <th style={ts.th}>{t("summary_jobs.col_model")}</th>
          <th style={ts.th}>{t("summary_jobs.col_tokens_in")}</th>
          <th style={ts.th}>{t("summary_jobs.col_tokens_out")}</th>
          <th style={ts.th}>{t("summary_jobs.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((job) => (
          <JobRow
            key={job.id}
            job={job}
            isExpanded={expandedId === job.id}
            onToggle={onToggle}
            onRetryDone={onRetryDone}
          />
        ))}
      </tbody>
    </table>
  );
}

function JobRow({
  job,
  isExpanded,
  onToggle,
  onRetryDone,
}: {
  job: SummaryJob;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onRetryDone: () => void;
}): React.JSX.Element {
  return (
    <>
      <tr
        onClick={() => onToggle(job.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onToggle(job.id);
        }}
        tabIndex={0}
        aria-expanded={isExpanded}
        style={{ ...ts.row, cursor: "pointer" }}
      >
        <td style={ts.td}>
          <div style={styles.targetType}>{job.target_type}</div>
          <div style={styles.mono}>{job.target_id.slice(0, 8)}</div>
        </td>
        <td style={ts.td}>
          <span style={styles.mono}>{job.user_id.slice(0, 8)}</span>
        </td>
        <td style={ts.td}>
          <StatusBadge status={job.status} />
        </td>
        <td style={ts.td}>{job.model ?? "-"}</td>
        <td style={ts.td}>{formatTokens(job.token_usage_in)}</td>
        <td style={ts.td}>{formatTokens(job.token_usage_out)}</td>
        <td style={ts.td}>{formatDate(job.created_at)}</td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} style={ts.expandedCell}>
            <SummaryJobDetailPanel
              jobId={job.id}
              isFailed={job.status === "failed"}
              onRetryDone={onRetryDone}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const colorMap: Record<string, string> = {
    pending: "var(--color-text-secondary)",
    running: "var(--color-primary)",
    completed: "var(--color-success)",
    failed: "var(--color-danger)",
  };
  const color = colorMap[status] ?? "var(--color-text-secondary)";
  return <span style={{ ...ts.badge, color }}>{status}</span>;
}

// ─── Helpers ───

function formatTokens(n: number | null): string {
  if (n === null) return "-";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Page-specific styles ───

const styles: Record<string, React.CSSProperties> = {
  controls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  searchForm: { display: "flex", gap: 8 },
  targetType: { fontWeight: 500 },
  mono: {
    fontSize: 12,
    fontFamily: "monospace",
    color: "var(--color-text-secondary)",
  },
};
